/**
 * The control panel and the HTML poster overlay: reads `index.html` into a
 * `RenderConfig`, and owns every piece of chrome the scene touches. Validation
 * mirrors the ranges the Python CLI enforces, so the web build cannot ask for a
 * picture the reference implementation would refuse.
 *
 * The typography lives in the DOM rather than in the framebuffer: it stays
 * crisp at any zoom, and the city labels can follow the orbiting scene.
 */

import { DEFAULT_CONFIG } from "../shared/types.ts";
import type { RenderConfig } from "../shared/types.ts";

/** One collision-resolved label as the scene reports it, in CSS pixels. */
export interface SceneLabel {
  name: string;
  x: number;
  y: number;
  anchorX: number;
  anchorY: number;
  population: number;
  visible: boolean;
}

function required<T extends Element>(id: string): T {
  const element = document.getElementById(id);
  if (!element) throw new Error(`Elemento #${id} mancante nella pagina.`);
  return element as unknown as T;
}

export const elements = {
  country: required<HTMLInputElement>("country"),
  cities: required<HTMLInputElement>("cities"),
  spikes: required<HTMLInputElement>("spikes"),
  year: required<HTMLInputElement>("year"),
  heightScale: required<HTMLInputElement>("height-scale"),
  title: required<HTMLInputElement>("title"),
  exposure: required<HTMLInputElement>("exposure"),
  aperture: required<HTMLInputElement>("aperture"),
  sunAzimuth: required<HTMLInputElement>("sun-azimuth"),
  sunElevation: required<HTMLInputElement>("sun-elevation"),
  viewTop: required<HTMLButtonElement>("view-top"),
  viewPerspective: required<HTMLButtonElement>("view-perspective"),
  render: required<HTMLButtonElement>("render"),
  download: required<HTMLButtonElement>("download"),
  progress: required<HTMLElement>("progress"),
  stats: required<HTMLElement>("stats"),
  canvas: required<HTMLCanvasElement>("canvas"),
  titleBlock: required<HTMLElement>("title-block"),
  countryName: required<HTMLElement>("country-name"),
  labels: required<HTMLElement>("labels"),
  leaders: required<SVGSVGElement>("leaders"),
  creditSource: required<HTMLElement>("credit-source"),
};

const inputs: HTMLInputElement[] = [
  elements.country,
  elements.cities,
  elements.spikes,
  elements.year,
  elements.heightScale,
  elements.title,
];

/**
 * The optical controls, which are deliberately *not* part of `RenderConfig`: none
 * of them re-enters the worker. Exposure and aperture are camera settings and the
 * sun is a scene setting, so all three apply to whatever is already on screen.
 */
export interface Finish {
  exposure: number;
  aperture: number;
  sunAzimuth: number;
  sunElevation: number;
}

export function readFinish(): Finish {
  return {
    exposure: Number(elements.exposure.value),
    aperture: Number(elements.aperture.value),
    sunAzimuth: Number(elements.sunAzimuth.value),
    sunElevation: Number(elements.sunElevation.value),
  };
}

/** Throws with a user-facing message; the caller shows it in `#progress`. */
export function readConfig(): { country: string; config: RenderConfig } {
  const country = elements.country.value.trim();
  if (!country) throw new Error("Indica lo Stato: nome o codice ISO, per esempio Italia, IT, ITA.");

  const heightScale = decimal(elements.heightScale.value, "L'altezza degli spike");
  if (!(heightScale >= 0.15 && heightScale <= 5)) {
    throw new Error("L'altezza degli spike deve essere compresa tra 0.15 e 5.");
  }

  const cities = integer(elements.cities.value, "Il numero di città");
  if (cities < 0 || cities > 50) {
    throw new Error("Il numero di città deve essere compreso tra 0 e 50.");
  }

  const maxSpikes = integer(elements.spikes.value, "Il numero di spike");
  if (maxSpikes !== 0 && (maxSpikes < 2_000 || maxSpikes > 500_000)) {
    throw new Error(
      "Gli spike devono essere 0 (tutte le celle popolate) oppure compresi tra 2000 e 500000.",
    );
  }

  const year = integer(elements.year.value, "L'anno WorldPop");
  if (year < 2000 || year > 2020) {
    throw new Error("L'anno WorldPop deve essere compreso tra 2000 e 2020.");
  }

  return {
    country,
    config: {
      // The orbit is the user's now; this only names the preset the camera
      // starts from and the one the download filename records.
      view: DEFAULT_CONFIG.view,
      heightScale,
      maxSpikes,
      cities,
      title: elements.title.checked,
      width: DEFAULT_CONFIG.width,
      height: DEFAULT_CONFIG.height,
      dpi: DEFAULT_CONFIG.dpi,
      seed: DEFAULT_CONFIG.seed,
      year,
    },
  };
}

export function setProgress(text: string, fraction?: number): void {
  const known = fraction !== undefined && Number.isFinite(fraction);
  const share = known ? Math.min(Math.max(fraction, 0), 1) : 0;
  elements.progress.textContent = known ? `${text} · ${Math.round(share * 100)}%` : text;
  elements.progress.style.setProperty("--fraction", share.toFixed(4));
  elements.progress.dataset.determinate = known ? "true" : "false";
}

export function setStats(text: string): void {
  elements.stats.textContent = text;
}

export function setBusy(busy: boolean): void {
  elements.render.disabled = busy;
  for (const input of inputs) input.disabled = busy;
  elements.progress.setAttribute("aria-busy", busy ? "true" : "false");
  document.body.dataset.busy = busy ? "true" : "false";
}

export function setTitleBlock(countryName: string, visible: boolean): void {
  elements.countryName.textContent = countryName;
  elements.titleBlock.hidden = !visible;
}

/** The credit line carries the data year, so it follows `#year`. */
export function setCredit(year: number): void {
  elements.creditSource.textContent = `Worldpop ${year} · Geoboundaries`;
}

const SVG_NAMESPACE = "http://www.w3.org/2000/svg";
const nodes: HTMLSpanElement[] = [];
const leaders: SVGGElement[] = [];
/** Last written placement per node, so a still scene writes no attributes. */
const placedX: number[] = [];
const placedY: number[] = [];
const placedAnchorX: number[] = [];
const placedAnchorY: number[] = [];

/**
 * Syncs the reusable DOM labels and SVG leaders to the collision-resolved scene
 * layout. Transforms avoid layout; line attributes change only with the camera.
 */
export function renderLabels(labels: ReadonlyArray<SceneLabel>): void {
  for (let index = nodes.length; index < labels.length; index += 1) {
    const node = document.createElement("span");
    node.hidden = true;
    nodes.push(node);
    elements.labels.append(node);

    const group = document.createElementNS(SVG_NAMESPACE, "g");
    const line = document.createElementNS(SVG_NAMESPACE, "line");
    const dot = document.createElementNS(SVG_NAMESPACE, "circle");
    dot.setAttribute("r", "2.2");
    group.append(line, dot);
    group.style.display = "none";
    leaders.push(group);
    elements.leaders.append(group);

    placedX.push(Number.NaN);
    placedY.push(Number.NaN);
    placedAnchorX.push(Number.NaN);
    placedAnchorY.push(Number.NaN);
  }

  for (let index = 0; index < nodes.length; index += 1) {
    const node = nodes[index];
    const leader = leaders[index];
    const label = index < labels.length ? labels[index] : null;
    if (!label || !label.visible) {
      if (!node.hidden) node.hidden = true;
      if (leader.style.display !== "none") leader.style.display = "none";
      continue;
    }
    if (node.textContent !== label.name) node.textContent = label.name;
    const x = Math.round(label.x);
    const y = Math.round(label.y);
    const anchorX = Math.round(label.anchorX);
    const anchorY = Math.round(label.anchorY);
    if (
      x !== placedX[index] ||
      y !== placedY[index] ||
      anchorX !== placedAnchorX[index] ||
      anchorY !== placedAnchorY[index]
    ) {
      placedX[index] = x;
      placedY[index] = y;
      placedAnchorX[index] = anchorX;
      placedAnchorY[index] = anchorY;
      node.style.transform = `translate(${x}px, ${y}px) translate(-50%, -50%)`;
      const line = leader.children[0] as SVGLineElement;
      const dot = leader.children[1] as SVGCircleElement;
      line.setAttribute("x1", String(anchorX));
      line.setAttribute("y1", String(anchorY));
      line.setAttribute("x2", String(x));
      line.setAttribute("y2", String(y));
      dot.setAttribute("cx", String(anchorX));
      dot.setAttribute("cy", String(anchorY));
    }
    if (node.hidden) node.hidden = false;
    if (leader.style.display === "none") leader.style.display = "";
  }
}

function decimal(raw: string, label: string): number {
  // A comma is what an Italian keyboard produces for a decimal point.
  const value = Number(raw.trim().replace(",", "."));
  if (!Number.isFinite(value)) throw new Error(`${label} non è un numero valido.`);
  return value;
}

function integer(raw: string, label: string): number {
  const value = decimal(raw, label);
  if (!Number.isInteger(value)) throw new Error(`${label} deve essere un numero intero.`);
  return value;
}
